import React from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import DataComponent from "Core/components/DataComponent";
import {getArray, isset} from "Core/helpers/data";
import {fetchStudyTypesAction} from "./actions";
import {isSuccessful} from "Core/helpers/io";
import SelectInput from "Core/components/input/SelectInput";
import {omit} from "lodash";
import {AsyncMountError} from "Core/errors";

class StudyTypesSelectInput extends DataComponent {
	constructor(props) {
		super(props, {
			data: {
				/**
				 * List of loaded types
				 * @type {Object[]}
				 */
				types: [],
				/**
				 * Flag that shows if types are being loaded
				 * @type {boolean}
				 */
				typesLoading: false,
			}
		}, {
			disableLoad: true,
		});
	}

	/**
	 * Replacement for default 'componentDidMount' method that will return a promise
	 * @note This method should be used instead of the default 'componentDidMount' when you need to have async calls in
	 * your 'componentDidMount'.
	 * @important Please remember to decrease the value of this.mountCount once async calls finish.
	 *
	 * @param {boolean} [override=false] - Flag that determines if this method should be executed in the 'override' mode.
	 * @note Override mode is reserved for calls by the child 'componentDidMount' methods that override this method to
	 * enable overriding the data loading functionality but still executing the base component's 'componentDidMount' that
	 * handles core functionality like adding registered event listeners.
	 * @return {Promise<number|undefined>} Promise that will resolve with the updated mount count that
	 * will be set in the 'componentDidMount' method or undefined for default functionality where 'componentDidMount'
	 * will just reset the mount count to zero.
	 * @throws {AsyncMountError} Promise can reject with the AsyncMountError in which case another
	 * 'asyncComponentDidMount' will be called if mount count is greater than zero.
	 */
	async asyncComponentDidMount(override = false) {
		// Call the parent component's 'asyncComponentDidMount' method that handles core functionality
		await super.asyncComponentDidMount(override);

		const {options, studyId, fetchStudyTypesAction} = this.props;

		if (!isset(options)) {
			await this.setValue('typesLoading', true)
				.then(() => this.executeAbortableAction(fetchStudyTypesAction, studyId))
				.then(response => {
					if (!isset(response)) {
						throw new AsyncMountError();
					} else if (isSuccessful(response)) {
						return this.setValue('types', getArray(response, 'data'))
							.then(() => this.setValue('typesLoading', false))
					} else if (isset(response)) {
						return this.setValue('typesLoading', false);
					}
				})
				.catch(async (error) => {
					await this.setValue('typesLoading', false);
					
					if (error instanceof AsyncMountError) throw error;
					else return Promise.resolve();
				});
		} else {
			await this.setValue('types', getArray(options));
		}
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		// Get study types used as options
		const options = getArray(this.getValue('types'));

		return (
			<SelectInput
				primaryKey="id"
				options={options}
				isLoading={this.getValue('typesLoading')}
				getOptionLabel={o => (
					// Use i18n to get the type name used as a label but fall back to the study type name field
					this.hasTranslation(o.code, 'constants.studyTypes') ?
						this.t(o.code, 'constants.studyTypes') :
						o.name
				)}
				getOptionValue={o => o.id}
				{...omit(this.props, 'fetchStudyTypesAction')}
			/>
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
StudyTypesSelectInput.propTypes = {
	...SelectInput.propTypes,
	
	// ID of the study that will be used to fetch the study types
	studyId: PropTypes.string,
};

export default connect(null, {fetchStudyTypesAction})(StudyTypesSelectInput);